import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { Book, UserReview, UserReviewStatus } from 'types';
import { UserReviewChanges } from 'api';
import {
  Button,
  Cover,
  EmptyState,
  Select,
  SelectOption,
  StarRating,
} from 'components';
import { useAuth } from '../../contexts/auth-context';
import { useModerateUserReview, useUserReviews } from '../../hooks';
import { ReviewEditor } from './review-editor';
import styles from './moderation.module.scss';

const SEARCH_DELAY = 300;

const STATUSES: { id: UserReviewStatus | 'all'; label: string }[] = [
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'all', label: 'All' },
];

type ModerationPageProps = {
  books: Book[];
  onBookSelect: (book: Book) => void;
};

export const ModerationPage = ({
  books,
  onBookSelect,
}: ModerationPageProps) => {
  const { isAdmin } = useAuth();
  const [status, setStatus] = useState<UserReviewStatus | 'all'>('pending');
  const [bookId, setBookId] = useState(0);
  const [authorInput, setAuthorInput] = useState('');
  const [author, setAuthor] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAuthor(authorInput.trim());
      setPage(1);
    }, SEARCH_DELAY);

    return () => clearTimeout(timer);
  }, [authorInput]);

  const filters = {
    status: status === 'all' ? undefined : status,
    bookId: bookId || undefined,
    author: author || undefined,
  };
  const { data, isPending, isFetching, isError } = useUserReviews(
    page,
    filters,
    isAdmin,
  );
  const { approve, remove, update } = useModerateUserReview();

  const booksById = useMemo(
    () => new Map(books.map((book) => [book.id, book])),
    [books],
  );
  const bookOptions = useMemo(
    () => [
      { value: 0, label: 'All books' },
      ...books
        .map((book) => ({
          value: book.id,
          label: book.title,
          sublabel: book.author,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [books],
  );

  if (!isAdmin) {
    return (
      <div className={styles.denied}>
        <h2 className={styles.title}>Access denied</h2>
        <p className={styles.subtitle}>
          This page is only available for administrators.
        </p>
      </div>
    );
  }

  const reviews = data?.reviews ?? [];
  const lastPage = data?.lastPage ?? 1;

  const renderList = () => {
    if (isPending) {
      return (
        <div className={styles.list}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className={cn(styles.card, styles.skeleton)} />
          ))}
        </div>
      );
    }

    if (isError) {
      return (
        <EmptyState
          icon="⚠️"
          title="Could not load reviews"
          subtitle="Check that you're logged in and try again."
        />
      );
    }

    if (reviews.length === 0) {
      return (
        <EmptyState
          icon="📭"
          title="Nothing here"
          subtitle={
            status === 'pending'
              ? 'No reviews are waiting for moderation.'
              : 'No reviews match these filters.'
          }
        />
      );
    }

    return (
      <div className={cn(styles.list, { [styles.stale]: isFetching })}>
        {reviews.map((review) => (
          <ModerationCard
            key={review.id}
            review={review}
            book={booksById.get(review.bookId)}
            isApproving={
              approve.isPending && approve.variables?.id === review.id
            }
            approveFailed={
              approve.isError && approve.variables?.id === review.id
            }
            isDeleting={remove.isPending && remove.variables === review.id}
            deleteFailed={remove.isError && remove.variables === review.id}
            isSaving={update.isPending && update.variables?.id === review.id}
            saveFailed={update.isError && update.variables?.id === review.id}
            bookOptions={bookOptions.slice(1)}
            onApprove={() => approve.mutate(review)}
            onDelete={() => remove.mutate(review.id)}
            onSave={(changes, onDone) =>
              update.mutate({ id: review.id, changes }, { onSuccess: onDone })
            }
            onBookSelect={onBookSelect}
          />
        ))}
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2 className={styles.title}>Reader reviews</h2>
        <p className={styles.subtitle}>
          Approve a review to publish it on the site, or delete it.
        </p>
      </div>
      <div className={styles.filters}>
        <div className={styles.statuses} role="group" aria-label="Status">
          {STATUSES.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              className={cn(styles.chip, {
                [styles.chipActive]: status === id,
              })}
              aria-pressed={status === id}
              onClick={() => {
                setStatus(id);
                setPage(1);
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div className={styles.fields}>
          <input
            type="search"
            className={styles.search}
            value={authorInput}
            onChange={(e) => setAuthorInput(e.target.value)}
            placeholder="Search by reviewer…"
            aria-label="Search by reviewer"
          />
          <div className={styles.bookFilter}>
            <Select
              options={bookOptions}
              value={bookId}
              placeholder="All books"
              onChange={(value) => {
                setBookId(Number(value) || 0);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      {data && !isPending && (
        <p className={styles.count}>
          {data.total} {data.total === 1 ? 'review' : 'reviews'}
        </p>
      )}

      {renderList()}

      {lastPage > 1 && (
        <div className={styles.pager}>
          <Button
            variant="ghost"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ← Previous
          </Button>
          <span className={styles.pageInfo}>
            Page {page} of {lastPage}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={page >= lastPage}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </Button>
        </div>
      )}
    </div>
  );
};

type ModerationCardProps = {
  review: UserReview;
  book?: Book;
  isApproving: boolean;
  approveFailed: boolean;
  isDeleting: boolean;
  deleteFailed: boolean;
  isSaving: boolean;
  saveFailed: boolean;
  bookOptions: SelectOption[];
  onApprove: () => void;
  onDelete: () => void;
  onSave: (changes: UserReviewChanges, onDone: () => void) => void;
  onBookSelect: (book: Book) => void;
};

const ModerationCard = ({
  review,
  book,
  isApproving,
  approveFailed,
  isDeleting,
  deleteFailed,
  isSaving,
  saveFailed,
  bookOptions,
  onApprove,
  onDelete,
  onSave,
  onBookSelect,
}: ModerationCardProps) => {
  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  const title = book?.title ?? review.book?.title ?? `Book #${review.bookId}`;
  const bookAuthor = book?.author ?? review.book?.author;

  return (
    <article className={styles.card}>
      {book && (
        <button
          type="button"
          className={styles.cover}
          onClick={() => onBookSelect(book)}
          aria-label={`Open ${book.title}`}
        >
          <Cover book={book} size="sm" />
        </button>
      )}
      <div className={styles.body}>
        <div className={styles.top}>
          <div>
            <h3 className={styles.bookTitle}>{title}</h3>
            {bookAuthor && (
              <span className={styles.bookAuthor}>{bookAuthor}</span>
            )}
          </div>
          <span className={cn(styles.status, styles[review.status])}>
            {review.status}
          </span>
        </div>

        {editing ? (
          <ReviewEditor
            review={review}
            bookOptions={bookOptions}
            isSaving={isSaving}
            saveFailed={saveFailed}
            onSave={(changes) => onSave(changes, () => setEditing(false))}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <>
            <div className={styles.meta}>
              <span className={styles.reviewer}>{review.authorName}</span>
              <span>{review.date}</span>
              <span className={styles.rating}>
                <StarRating rating={review.rating} outOf={10} size={13} />
                {review.rating}/10
              </span>
              <span className={styles.language}>{review.language}</span>
            </div>

            <p className={styles.text}>{review.text}</p>

            {review.moderationNote && (
              <p className={styles.note}>Note: {review.moderationNote}</p>
            )}

            <div className={styles.actions}>
              {(approveFailed || deleteFailed) && (
                <span className={styles.error}>
                  {approveFailed ? 'Could not approve' : 'Could not delete'}
                </span>
              )}
              {confirming ? (
                <>
                  <span className={styles.confirmText}>Delete for good?</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isDeleting}
                    onClick={() => setConfirming(false)}
                  >
                    Cancel
                  </Button>
                  <Button size="sm" loading={isDeleting} onClick={onDelete}>
                    Delete
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isApproving}
                    onClick={() => setConfirming(true)}
                  >
                    Delete
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isApproving}
                    onClick={() => setEditing(true)}
                  >
                    Edit
                  </Button>
                  {review.status !== 'approved' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={isApproving}
                      onClick={onApprove}
                    >
                      Approve & publish
                    </Button>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </article>
  );
};
